import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, TrendingUp, Users, Target, Brain, Award, Briefcase, Settings, Globe, Layers, Zap, BarChart3, PieChart, LineChart, UserCheck, Network, Workflow } from 'lucide-react';

const Home = () => {
  const offerings = [
    {
      icon: Building2,
      title: 'Organisational Effectiveness',
      description: 'Designing culture, structure, governance, and capability to support scale and execution.',
      link: '/services#org-effectiveness',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Target,
      title: 'Pre-Investment HR Due Diligence',
      description: 'Context-driven assessment of people and leadership risk across growth-stage investments.',
      link: '/services#due-diligence',
      color: 'from-emerald-500 to-emerald-600'
    },
    {
      icon: TrendingUp,
      title: 'Fractional CHRO Services',
      description: 'Senior HR leadership on demand - strategic depth without full-time commitment.',
      link: '/services#fractional-chro',
      color: 'from-amber-500 to-amber-600'
    },
    {
      icon: Users,
      title: 'CXO & Critical Leadership Search',
      description: 'Leadership hiring aligned to strategy, stage, and scale ambition.',
      link: '/services#leadership-search',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: Brain,
      title: 'HR Technology Advisory (AI-Enabled)',
      description: 'AI-enabled HR platforms that reduce dependency and deliver decision-grade insight.',
      link: '/services#hr-technology',
      color: 'from-rose-500 to-rose-600'
    },
    {
      icon: Award,
      title: 'Manager Capability & Coaching',
      description: 'Targeted coaching and development to strengthen leadership depth and execution.',
      link: '/services#manager-capability',
      color: 'from-cyan-500 to-cyan-600'
    }
  ];

  const provenOutcomes = [
    {
      icon: Globe,
      result: 'Enterprise-wide cultural transformation and leadership multiplier journeys',
      client: 'Global Retail Organisation | GCC',
      color: 'bg-blue-50 border-blue-200'
    },
    {
      icon: PieChart,
      result: '₹5.5 Cr in post-merger optimisation savings',
      client: 'High-growth Vaccine Manufacturer | India',
      color: 'bg-emerald-50 border-emerald-200'
    },
    {
      icon: Zap,
      result: '95% reduction in HR operational effort through AI agents',
      client: 'Global FMCG | Singapore',
      color: 'bg-amber-50 border-amber-200'
    },
    {
      icon: Layers,
      result: 'Standardised talent acquisition across India, MENA, and Africa',
      client: 'Top 3 Global Logistics Player',
      color: 'bg-purple-50 border-purple-200'
    },
    {
      icon: BarChart3,
      result: '60% reduction in HRBP load and ₹13.4 Cr in sales optimisation impact',
      client: 'Leading Pharmaceutical Company | India',
      color: 'bg-rose-50 border-rose-200'
    },
    {
      icon: LineChart,
      result: 'Succession planning and leadership hiring supporting scale from 700 to 15,000 employees',
      client: 'Diversified BPM Organisation | India',
      color: 'bg-cyan-50 border-cyan-200'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] pt-32 pb-24 lg:pt-44 lg:pb-32 overflow-hidden bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=2070&q=80"
            alt="Modern corporate architecture"
            className="w-full h-full object-cover opacity-15" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight tracking-tight">
              Building Organisational Readiness at
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-500">
                Critical Moments
              </span>
            </h1>
            <p className="lg:text-2xl max-w-3xl font-light text-xl mb-12 text-slate-300">
              For Investors, Founders, CEOs and CHROs preparing organisations for scale.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact#ready-to-move"
                className="inline-flex items-center justify-center px-8 py-4 bg-amber-500 text-slate-900 font-semibold rounded-md hover:bg-amber-400 transition-all duration-200 shadow-lg group">
                Start a Conversation
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-md hover:bg-white/20 transition-all duration-200 border border-white/20">
                Explore Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Is Your Organisation Ready Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                Is your organisation approaching its next inflection point?
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto mb-4">
                There comes a stage where growth is no longer driven by momentum alone.
              </p>
              <p className="text-lg text-slate-700 font-medium">
                Execution quality becomes decisive - and with it, the organisation's underlying people capability.
              </p>
            </div>

            <div className="text-center mb-12">
              <p className="text-xl text-amber-600 font-semibold">
                At this point, three factors begin to matter more than anything else:
              </p>
            </div>

            {/* Three Pillars with Images */}
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <div className="bg-white border border-blue-100 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
                <div className="h-56 overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_1549dae8-750d-40d4-95b1-202e48d84d83/artifacts/ym59j3pi_Image%205%20-%20Home%20-%20Leadership%20Depth.jpg"
                    alt="Leadership Depth"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Leadership Depth</h3>
                  <p className="text-slate-600 leading-relaxed">
                    The organisation can no longer rely on a small group of founders or long-tenured leaders. Decision ownership must be clear, leadership capability distributed, and bench strength built to support scale.
                  </p>
                </div>
              </div>

              <div className="bg-white border border-emerald-100 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
                <div className="h-56 overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_1549dae8-750d-40d4-95b1-202e48d84d83/artifacts/eunredbv_Image%206%20-%20Home%20-%20People%20Choices.jpg"
                    alt="People Choices"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">People Choices</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Hiring, role design, and deployment need to reflect where the business is going - not where it has been. Performance, timing, and role fit outweigh tenure or familiarity.
                  </p>
                </div>
              </div>

              <div className="bg-white border border-amber-100 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
                <div className="h-56 overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_1549dae8-750d-40d4-95b1-202e48d84d83/artifacts/oof3zero_Image%207%20-%20Home%20-%20Scalable%20Systems.jpg"
                    alt="Scalable Systems"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Scalable Systems</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Processes, governance, and operating rhythms must support growth without adding friction - enabling repeatable execution while preserving control.
                  </p>
                </div>
              </div>
            </div>

            {/* Inflection Points & Partnership */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 lg:p-12">
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                These inflection points typically emerge when organisations are scaling toward the next milestone, preparing for investment, IPO, or acquisition, or professionalising a promoter - or founder-led business.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                At NexStep HR Solutions, we partner with promoters, Boards, CXOs, CHROs, and investors precisely at these moments - when ambition is clear, complexity is increasing, and the next phase of growth requires a deliberate, execution-ready people strategy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* People Shape Every Transformation */}
      <section className="py-20 lg:py-28 bg-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 leading-tight">
              People Shape Every Transformation
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Across sectors and stages, one fact holds...
            </p>
            <p className="font-semibold text-xl mt-4 text-amber-600">
              Sustainable growth is built through People Capability.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div className="relative order-2 lg:order-1">
              <img
                src="https://customer-assets.emergentagent.com/job_1549dae8-750d-40d4-95b1-202e48d84d83/artifacts/k2rrvflb_Image%204%20-%20Home%20-%20People%20Shape%20Transformation.jpg"
                alt="People shaping transformation"
                className="rounded-xl shadow-xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Most HR transformations don't fail due to lack of effort - they fail because they overlook context, underestimate culture, and leave ownership unclear. The result is change that looks coherent in theory, but breaks in execution.
              </p>
              <div className="p-6 bg-white border border-slate-200 rounded-xl shadow-sm">
                <p className="text-amber-600 font-bold text-xl mb-3">Our role is to cut through complexity</p>
                <p className="text-slate-600 leading-relaxed">
                  We design people and HR transformations that are business-anchored, context-specific, and execution-ready - aligned to how organisations actually operate, grow, and lead.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why NexStep HR Solutions */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 leading-tight">
              Why NexStep HR Solutions
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-4">
              Transformation is not about doing more.
            </p>
            <p className="text-lg text-slate-700 font-medium mb-4">
              It is about doing what matters - sequenced correctly and owned decisively.
            </p>
            <p className="text-slate-600">
              NexStep HR Solutions exists to bridge the gap between intent and execution, helping organisations turn people strategy into operating reality.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="p-8 bg-gradient-to-br from-blue-50 to-slate-50 border border-blue-100 rounded-xl hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                <Briefcase className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">From Strategy to Execution</h3>
              <p className="text-slate-600 leading-relaxed">Built to be implemented and sustained - not designed in isolation.</p>
            </div>
            <div className="p-8 bg-gradient-to-br from-emerald-50 to-slate-50 border border-emerald-100 rounded-xl hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                <Settings className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Context Before Templates</h3>
              <p className="text-slate-600 leading-relaxed">Every intervention reflects your stage, ambition, and organisational reality.</p>
            </div>
            <div className="p-8 bg-gradient-to-br from-amber-50 to-slate-50 border border-amber-100 rounded-xl hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                <Brain className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Human Judgment | Enhanced by Data and AI</h3>
              <p className="text-slate-600 leading-relaxed">Modern, balanced solutions that are measurable and decision-grade.</p>
            </div>
            <div className="p-8 bg-gradient-to-br from-purple-50 to-slate-50 border border-purple-100 rounded-xl hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                <Globe className="text-white" size={32} />
              </div>
              <h3 className="font-bold text-xl mb-4 text-slate-900">Global Perspective | Local Understanding</h3>
              <p className="text-slate-600 leading-relaxed">Experience across India, the Middle East, Singapore, the Far East, and Africa - applied with on-ground nuance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Six Core Transformation Areas */}
      <section className="py-20 lg:py-28 bg-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 leading-tight">
              Six Core Transformation Areas
            </h2>
            <p className="text-lg text-slate-600">
              Designed for moments that demand clarity and disciplined execution.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offerings.map((offering, index) => {
              const Icon = offering.icon;
              return (
                <Link
                  key={index}
                  to={offering.link}
                  className="group p-8 bg-white border border-slate-200 rounded-xl hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                  <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${offering.color}`}></div>
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 bg-gradient-to-r ${offering.color} shadow-lg`}>
                    <Icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4 group-hover:text-amber-600 transition-colors">
                    {offering.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-4 text-sm">{offering.description}</p>
                  <span className="inline-flex items-center text-amber-600 font-semibold text-sm group-hover:gap-2 transition-all">
                    Learn More
                    <ArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" size={16} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Built for Decision Makers */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 leading-tight">
              Built for Decision-Makers
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Partnering with leaders who treat people strategy as a core business discipline.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="group">
              <div className="relative overflow-hidden rounded-xl mb-6 h-56">
                <img 
                  src="https://customer-assets.emergentagent.com/job_1549dae8-750d-40d4-95b1-202e48d84d83/artifacts/mqdwkoqq_Image%2016%20-%20Home%20-%20PE%20%26%20VC%20Investors.jpg" 
                  alt="PE & VC Investors"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-xl font-bold text-white mb-2">PE & VC Investors</h3>
                </div>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Assessing people risk and leadership readiness across portfolio companies - pre/post investment.
              </p>
            </div>

            <div className="group">
              <div className="relative overflow-hidden rounded-xl mb-6 h-56">
                <img 
                  src="https://customer-assets.emergentagent.com/job_1549dae8-750d-40d4-95b1-202e48d84d83/artifacts/xb3b3hdx_Image%2017%20-%20Home%20-%20Founders%20and%20Promoters.jpg" 
                  alt="Founders & Promoters"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-xl font-bold text-white mb-2">Founders & Promoters</h3>
                </div>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Supporting scalable organisation building - from early growth through Series C and beyond.
              </p>
            </div>

            <div className="group">
              <div className="relative overflow-hidden rounded-xl mb-6 h-56">
                <img 
                  src="https://customer-assets.emergentagent.com/job_1549dae8-750d-40d4-95b1-202e48d84d83/artifacts/4ubrfsmc_Image%2018%20-%20Home%20-%20CXOs%20%26%20Board%20Members.jpg" 
                  alt="CXOs & Board Members"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-xl font-bold text-white mb-2">CXOs & Board Members</h3>
                </div>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Advising leaders through transformation, succession and organisational restructuring.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Proven Outcomes */}
      <section className="py-20 lg:py-28 bg-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 leading-tight">
              Proven Outcomes
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Our work has delivered measurable impact across industries, stages, and geographies.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {provenOutcomes.map((outcome, index) => {
              const Icon = outcome.icon;
              return (
                <div key={index} className={`p-6 rounded-xl border ${outcome.color} hover:shadow-lg transition-shadow`}>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                      <Icon className="text-slate-700" size={24} />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 mb-2">{outcome.result}</p>
                      <p className="text-sm text-slate-500 font-medium">{outcome.client}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 leading-tight">
              Ready to take the NexStep?
            </h2>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed max-w-2xl mx-auto">
              We welcome a brief discussion on organisational readiness and scale.
            </p>
            <Link
              to="/contact#ready-to-move"
              className="inline-flex items-center px-8 py-4 bg-amber-500 text-slate-900 font-semibold rounded-md hover:bg-amber-400 transition-all duration-200 shadow-lg group">
              Schedule a Conversation
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
